import api from './client';

export type Alarm = {
  id: number;
  dateid: number;
  date_actual: string;
  alarm_source_id: number;
  alarm_source_name: string;
  power_plant_id: number;
  power_plant_name: string;
  inverter_id: number;
  inverter_name: string;
  string_number: string;
  received_code: string;
  received_code_count: number;
  description: string;
  priority: number;
  priority_label: string;
  uuid_id: string;
  ignore_alarm: number;
  ignored_by: string | null;
  ignored_on: string | null;
};

export async function fetchAlarms(): Promise<Alarm[]> {
  const res = await api.get<Alarm[]>('/alarms');
  return res.data;
}

export async function fetchAlarmDetail(uuid_id: string): Promise<Alarm> {
  const res = await api.get<Alarm>(`/alarms/${uuid_id}`);
  return res.data;
}

export async function ignoreAlarm(id: number): Promise<void> {
  await api.patch(`/alarms/${id}/ignore`);
}

export type AlarmCounts = {
  daily_warning_count: number;
  daily_minor_count: number;
  daily_major_count: number;
  daily_total_count: number;
  weekly_warning_count: number;
  weekly_minor_count: number;
  weekly_major_count: number;
  weekly_total_count: number;
  monthly_warning_count: number;
  monthly_minor_count: number;
  monthly_major_count: number;
  monthly_total_count: number;
};

export type RawAlarm = {
  date_yyyymmdd: number;
  date_actual: string;
  alarm_priority: number;
  priority_label: string;
  alarm_source_name: string;
  alarm_power_plant_name: string;
  alarm_inverter_name: string;
  alarm_string_number: string | null;
  alarm_code: string;
  alarm_description: string;
  alarm_email_status: number;
};

export type ActiveAlarm = {
  date_iso: string;
  created_on: string;
  alarm_priority: number;
  priority_label: string;
  alarm_source_name: string;
  alarm_power_plant_name: string;
  alarm_inverter_name: string;
  alarm_string_number: string | null;
  alarm_code: string;
  alarm_description: string;
  status_description: string;
  alarm_email_status: number;
};

export type NewLastHourAlarm = {
  first_seen: string;
  last_seen: string;
  alarm_power_plant_name: string;
  alarm_inverter_name: string;
  alarm_code: string;
};

export type NewLastHourAlarmResponse = {
  count: number;
  rows: NewLastHourAlarm[];
  summary?: { total: number; warning: number; minor: number; major: number };
};

export async function fetchActiveAlarms(params?: { plant_uuid?: string }): Promise<ActiveAlarm[]> {
  const res = await api.get<ActiveAlarm[]>('/alarms/active', { params });
  return res.data;
}

export async function fetchNewActiveAlarmsLastHour(): Promise<NewLastHourAlarmResponse> {
  const res = await api.get<NewLastHourAlarmResponse>('/alarms/new-last-hour');
  return res.data;
}

export async function fetchAlarmCounts(): Promise<AlarmCounts> {
  const res = await api.get<AlarmCounts>('/alarms/counts');
  return res.data;
}

export async function fetchRawAlarms(): Promise<RawAlarm[]> {
  const res = await api.get<RawAlarmPageResponse>('/alarms/raw', {
    params: { range: '31days', page: 1, pageSize: 100 },
  });
  return res.data.rows;
}

export type RawAlarmPageResponse = {
  rows: RawAlarm[];
  total: number;
  page: number;
  pageSize: number;
};

export async function fetchRawAlarmsPaged(params: {
  range: 'today' | '7days' | '31days';
  page?: number;
  pageSize?: number;
  all?: boolean;
  search?: string;
  plant_uuid?: string;
}): Promise<RawAlarmPageResponse> {
  const res = await api.get<RawAlarmPageResponse>('/alarms/raw', { params });
  return res.data;
}

export type RawAlarmDetail = {
  created_on: string;
  alarm_priority: number;
  priority_label: string;
  alarm_source_name: string;
  alarm_power_plant_name: string;
  alarm_inverter_name: string;
  alarm_string_number: string | null;
  alarm_code: string;
  alarm_description: string;
  status_description: string;
  alarm_email_status: number;
};

export async function fetchRawAlarmDetail(params: {
  date: string;
  plant: string;
  inverter: string;
  code: string;
  source?: string;
}): Promise<RawAlarmDetail[]> {
  const res = await api.get<RawAlarmDetail[]>('/alarms/raw-detail', { params });
  return res.data;
}

export async function ignoreRawAlarm(params: {
  date: string;
  plant: string;
  inverter: string;
  code: string;
}): Promise<{ success: boolean; affected: number }> {
  const res = await api.patch('/alarms/raw-ignore', params);
  return res.data;
}
